import { InteractionTargetType } from 'types/error-log';
import { truncateString } from 'utils/error-log/serialize';

const MAX_TEXT_LENGTH = 80;
const MAX_PATH_DEPTH = 5;
const MAX_CLASSES_PER_SEGMENT = 2;
const INTERACTIVE_SELECTOR =
  'button, a, input, select, textarea, label, [role="button"], [role="tab"], [role="menuitem"], [role="option"]';

function attribute(element: Element, name: string): string | null {
  const value = element.getAttribute(name)?.trim();
  return value || null;
}

function describeSegment(element: Element): string {
  const tag = element.tagName.toLowerCase();
  if (element.id) {
    return `${tag}#${element.id}`;
  }
  const testId = attribute(element, 'data-testid');
  if (testId) {
    return `${tag}[data-testid=${testId}]`;
  }
  const classes = Array.from(element.classList)
    .slice(0, MAX_CLASSES_PER_SEGMENT)
    .join('.');
  return classes ? `${tag}.${classes}` : tag;
}

export function describeElementPath(element: Element): string {
  const segments: string[] = [];
  let current: Element | null = element;
  while (
    current &&
    current !== document.body &&
    segments.length < MAX_PATH_DEPTH
  ) {
    segments.unshift(describeSegment(current));
    current = current.parentElement;
  }
  return segments.join(' > ');
}

export function describeElement(
  target: EventTarget | null
): InteractionTargetType | null {
  if (!(target instanceof Element)) {
    return null;
  }
  const element = target.closest(INTERACTIVE_SELECTOR) ?? target;
  const text = (element.textContent ?? '').replace(/\s+/g, ' ').trim();
  return {
    tag: element.tagName.toLowerCase(),
    id: element.id || null,
    text: text ? truncateString(text, MAX_TEXT_LENGTH) : null,
    ariaLabel: attribute(element, 'aria-label'),
    name: attribute(element, 'name'),
    dataTestId: attribute(element, 'data-testid'),
    path: describeElementPath(element)
  };
}
